import type { ReactElement } from 'react'
import { useSearchParams } from 'react-router-dom'

import { UpdateAppPage } from '../pages/UpdateAppPage'
import { updatePlatformHeader } from '../services/api/auth'
import { setPlatformForLogs } from '../services/datadog'
import { redirectToUrl } from '../utils/location'
import { QueryParameters } from '../utils/queryParameters'

// Package id of the released Swile Android app. Matches the `applicationId` in the
// themenu-android app/build.gradle and the Play Store links the app builds itself.
const ANDROID_PACKAGE_ID = 'hr.lunc.client'
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_ID}`

export const UpdateAppRoute = (): ReactElement => {
  const [searchParams] = useSearchParams()

  const platform = 'android'
  updatePlatformHeader(platform)
  setPlatformForLogs(platform)

  const openPlayStore = (): void => {
    redirectToUrl(PLAY_STORE_URL)
  }

  // "I already updated" resumes the login the user already completed. POST /session
  // authenticated the account and set the session cookie *before* diverting here,
  // then forwarded its own success redirect (the post-login destination, marker
  // included) as `redirect_to`. We navigate straight to it — no form re-entry — and
  // fall back to the post-login success page. We must NOT clear auth cookies: that
  // would wipe the session and force the user to re-enter their credentials.
  //
  // `/update_app` is publicly routable and `redirect_to` is attacker-controllable,
  // so we constrain the target to our own origin (the server only ever forwards a
  // relative success_redirect_path) to avoid an open redirect off the auth domain.
  const relaunchLogin = (): void => {
    const redirectTo = searchParams.get(QueryParameters.REDIRECT_TO)
    const fallback = new URL('/session/success', window.location.origin)
    const target = new URL(redirectTo ?? '/session/success', window.location.origin)
    const safe = target.origin === window.location.origin ? target : fallback
    redirectToUrl(safe.href)
  }

  return <UpdateAppPage onUpdate={openPlayStore} onRelaunch={relaunchLogin} />
}
